import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { getEventDetail, resendExternalInvites, type ExternalAttendeeDto, type ExternalInviteInfo } from '../../api/calendar';
import { toast } from '../../components/shared/Toast';
import { tCalendar } from '../../lib/calendar-i18n';
import {
  externalDisplayName, externalResultNotices, externalUnavailableText, lastInviteLabel, needsResend,
  noteExternalSupport, rememberExternalSent, resendTargets,
} from './lib/externalAttendees';
import './AttendeeContacts.css';

/**
 * 일정 상세의 「외부 참석자」 묶음 (2026-11-02 · 지시 57).
 *
 * - 열 때 상세 GET 을 한 번 읽는다(목록 응답에는 외부 참석자가 없다). 서버가 이 기능을 모르거나 외부 참석자가
 *   없으면 «아무것도» 그리지 않는다 — 종전 상세 그대로.
 * - 사람마다 이름 · 회사 · 이메일과 마지막 메일 상태(보내는 중 · 보냄 · 실패 · 보내지 않음과 까닭).
 * - 작성자에게만 「다시 보내기」(못 간 사람이 있을 때). 같은 일정은 1분에 한 번 — 409 면 「잠시 후」를 띄운다.
 * - 읽은 결과로 «보낸 적 있나»를 기억해 둔다 — 삭제 확인 창이 「취소 메일이 발송됩니다」를 붙인다.
 */
export function EventExternalAttendees({ eventId, isCreator }: { eventId: string; isCreator: boolean }) {
  const [list, setList] = useState<ExternalAttendeeDto[] | null>(null);
  const [info, setInfo] = useState<Pick<ExternalInviteInfo, 'available' | 'reason'> | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    let p: ReturnType<typeof getEventDetail>;
    try {
      p = getEventDetail(eventId);
    } catch {
      return () => { alive = false; };
    }
    p.then((d) => {
      if (!alive || !noteExternalSupport(d)) return;
      setList(d.externalAttendees ?? []);
      setInfo(d.externalInvite ?? null);
      rememberExternalSent(eventId, d.externalAttendees);
    }).catch(() => { /* 못 읽으면 묶음을 그리지 않는다(종전 상세 그대로) */ });
    return () => { alive = false; };
  }, [eventId]);

  /**
   * ⭐ 57B — 늘 사람을 골라 보낸다. 비우면 서버가 «남은 전원»(이미 받은 사람 포함)에게 다시 보내므로
   *   빈 목록으로는 부르지 않는다.
   */
  const resend = useCallback(async (attendeeIds: string[]) => {
    if (busy || attendeeIds.length === 0) return;
    setBusy(true);
    try {
      const env = await resendExternalInvites(eventId, attendeeIds);
      if (env.externalAttendees) {
        setList(env.externalAttendees);
        rememberExternalSent(eventId, env.externalAttendees);
      }
      if (env.externalInvite) setInfo({ available: env.externalInvite.available, reason: env.externalInvite.reason ?? null });
      for (const n of externalResultNotices(env)) (n.variant === 'error' ? toast.error : toast.info)(n.text);
    } catch (err) {
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      const code = axios.isAxiosError(err) ? (err.response?.data as { code?: unknown } | undefined)?.code : undefined;
      if (status === 409) toast.info(tCalendar('calendar.external.resend_cooldown'));
      else if (code === 'NO_EXTERNAL_ATTENDEES') toast.info(tCalendar('calendar.external.resend_none'));
      else toast.error(tCalendar('calendar.external.resend_failed'));
    } finally {
      setBusy(false);
    }
  }, [busy, eventId]);

  if (!list || list.length === 0) return null;
  const now = Date.now();
  const targets = resendTargets(list, now);
  const unavailable = externalUnavailableText(info);

  return (
    <section className="calendar-detail-external" data-testid="detail-external-attendees" aria-labelledby={`detail-external-${eventId}`}>
      <div className="calendar-detail-external-head">
        <span id={`detail-external-${eventId}`}>{tCalendar('calendar.external.heading')} · {list.length}</span>
        {isCreator && targets && targets.length > 1 && info?.available !== false && (
          <button
            type="button"
            className="calendar-detail-external-resend"
            disabled={busy}
            onClick={() => void resend(targets)}
            data-testid="detail-external-resend-all"
          >
            {busy ? tCalendar('calendar.external.resend_busy') : tCalendar('calendar.external.resend_all')}
          </button>
        )}
      </div>
      <ul className="calendar-detail-external-list">
        {list.map((a) => {
          const st = lastInviteLabel(a.lastInvite, now);
          const canResend = isCreator && info?.available !== false && needsResend(a.lastInvite, now);
          return (
            <li key={a.id} className="calendar-detail-external-item" data-testid="detail-external-item">
              <span className="calendar-external-badge">{tCalendar('calendar.attendee_panel.external_badge')}</span>
              <span className="calendar-detail-external-who">
                <span className="calendar-detail-external-name">{externalDisplayName(a)}</span>
                {a.company && <span className="calendar-detail-external-sub">{a.company}</span>}
                <span className="calendar-detail-external-sub">{a.email}</span>
              </span>
              <span className="calendar-detail-external-status" data-tone={st.tone} data-testid="detail-external-status">{st.text}</span>
              {canResend && (
                <button
                  type="button"
                  className="calendar-detail-external-resend"
                  disabled={busy}
                  onClick={() => void resend([a.id])}
                  aria-label={`${tCalendar('calendar.external.resend')} · ${externalDisplayName(a)}`}
                  data-testid="detail-external-resend"
                >
                  {tCalendar('calendar.external.resend')}
                </button>
              )}
            </li>
          );
        })}
      </ul>
      {unavailable && <p className="calendar-detail-external-note" role="note">{unavailable}</p>}
    </section>
  );
}
